import type { PackagingConfig, PackagingStructure } from '@/lib/api/services';

export type StockBreakdown = {
  cartons: number;
  sets: number;
  pieces: number;
  totalPieces: number;
  structureType: PackagingStructure;
};

type PackagingLike =
  | Pick<PackagingConfig, 'structureType' | 'piecesPerCarton' | 'setsPerCarton' | 'piecesPerSet'>
  | Record<string, unknown>
  | null
  | undefined;

function readPackaging(config?: PackagingLike) {
  if (!config) return null;
  const row = config as Record<string, unknown>;
  const structureType = (row.structureType || row.structure_type || 'piece') as PackagingStructure;
  return {
    structureType,
    piecesPerCarton: Number(row.piecesPerCarton ?? row.pieces_per_carton ?? 0),
    setsPerCarton: row.setsPerCarton ?? row.sets_per_carton ?? null,
    piecesPerSet: row.piecesPerSet ?? row.pieces_per_set ?? null,
  };
}

function pluralUnit(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

export type StockUnit = 'piece' | 'set' | 'carton';

/** CTN → SET (if configured) → PC, matching each product's stock setting. */
export function availableStockUnits(config?: PackagingLike): StockUnit[] {
  const structure: PackagingStructure = readPackaging(config)?.structureType || 'piece';
  if (structure === 'carton_set_piece') return ['carton', 'set', 'piece'];
  if (structure === 'carton_piece') return ['carton', 'piece'];
  return ['piece'];
}

export function defaultStockUnit(config?: PackagingLike): StockUnit {
  return availableStockUnits(config)[0];
}

function stockDisplayParts(b: StockBreakdown, compact: boolean): string[] {
  if (b.structureType === 'piece') {
    return [compact ? `${b.pieces.toLocaleString()} PC` : pluralUnit(b.pieces, 'PC', 'PCs')];
  }

  const parts: string[] = [];
  const empty = b.cartons === 0 && b.sets === 0 && b.pieces === 0;

  if (b.cartons > 0 || empty) {
    parts.push(compact ? `${b.cartons} CTN` : pluralUnit(b.cartons, 'Carton', 'Cartons'));
  }
  if (b.structureType === 'carton_set_piece' && b.sets > 0) {
    parts.push(compact ? `${b.sets} SET` : pluralUnit(b.sets, 'Set', 'Sets'));
  }
  if (b.pieces > 0) {
    parts.push(compact ? `${b.pieces} PC` : pluralUnit(b.pieces, 'PC', 'PCs'));
  }
  return parts;
}

/** Reduce piece quantity into Cartons → Sets → PCs. */
export function breakdownStockPieces(totalPieces: number, config: PackagingLike): StockBreakdown {
  const qty = Math.max(0, Math.floor(Number(totalPieces) || 0));
  const pkg = readPackaging(config);
  const structure: PackagingStructure = pkg?.structureType || 'piece';

  if (!pkg || structure === 'piece') {
    return {
      cartons: 0,
      sets: 0,
      pieces: qty,
      totalPieces: qty,
      structureType: 'piece',
    };
  }

  if (structure === 'carton_set_piece') {
    const setsPerCarton = Number(pkg.setsPerCarton || 0);
    const piecesPerSet = Number(pkg.piecesPerSet || 0);
    const ppc = setsPerCarton * piecesPerSet;
    if (ppc <= 0 || setsPerCarton <= 0 || piecesPerSet <= 0) {
      return {
        cartons: 0,
        sets: 0,
        pieces: qty,
        totalPieces: qty,
        structureType: structure,
      };
    }
    const cartons = Math.floor(qty / ppc);
    const rem = qty % ppc;
    const sets = Math.floor(rem / piecesPerSet);
    const pieces = rem % piecesPerSet;
    return {
      cartons,
      sets,
      pieces,
      totalPieces: qty,
      structureType: structure,
    };
  }

  const ppc = Number(pkg.piecesPerCarton || 0) || 1;
  const cartons = Math.floor(qty / ppc);
  const pieces = qty % ppc;
  return {
    cartons,
    sets: 0,
    pieces,
    totalPieces: qty,
    structureType: 'carton_piece',
  };
}

/**
 * e.g. "10 Cartons", "10 Cartons, 5 Sets", "10 Cartons, 5 Sets, 3 PCs"
 * Priority from product stock setting: CTN → Sets (if configured) → leftover PCs.
 */
export function formatStockDisplay(
  totalPieces: number,
  config?: PackagingLike,
  precomputed?: string | null
): string {
  const b = breakdownStockPieces(totalPieces, config);
  // Ignore stale "N PCs" labels when packaging can convert to CTN/SET.
  if (precomputed && b.structureType === 'piece') return precomputed;
  return stockDisplayParts(b, false).join(', ');
}

/**
 * Compact labels for dense tables: "12 CTN · 5 SET · 3 PC" or "48 PC".
 */
export function formatStockCompact(totalPieces: number, config?: PackagingLike): string {
  const b = breakdownStockPieces(totalPieces, config);
  return stockDisplayParts(b, true).join(' · ');
}

export type PriorityQty = {
  cartons: number;
  sets: number;
  pieces: number;
};

export function emptyPriorityQty(): PriorityQty {
  return { cartons: 0, sets: 0, pieces: 0 };
}

export function addPriorityQty(into: PriorityQty, totalPieces: number, config?: PackagingLike) {
  const b = breakdownStockPieces(Math.abs(Number(totalPieces) || 0), config);
  into.cartons += b.cartons;
  into.sets += b.sets;
  into.pieces += b.pieces;
}

/** Compact CTN → SET → PC for a piece quantity (optional +/−). */
export function formatPriorityQty(
  totalPieces: number,
  config?: PackagingLike,
  signed = false
): string {
  const n = Number(totalPieces) || 0;
  const label = formatStockCompact(Math.abs(n), config);
  if (!signed) return label;
  if (n > 0) return `+${label}`;
  if (n < 0) return `-${label}`;
  return label;
}

/** Combined warehouse counts: CTN first, then SET, then leftover PC. */
export function formatPriorityParts(parts: PriorityQty, signed?: number): string {
  const list: string[] = [];
  if (parts.cartons > 0) list.push(`${parts.cartons} CTN`);
  if (parts.sets > 0) list.push(`${parts.sets} SET`);
  if (parts.pieces > 0 || list.length === 0) list.push(`${parts.pieces} PC`);
  const label = list.join(' · ');
  if (signed == null) return label;
  if (signed > 0) return `+${label}`;
  if (signed < 0) return `-${label}`;
  return label;
}

export function formatSignedPriorityParts(parts: PriorityQty): string {
  const list: string[] = [];
  const push = (n: number, unit: string) => {
    if (!n) return;
    list.push(`${n > 0 ? '+' : '-'}${Math.abs(n)} ${unit}`);
  };
  push(parts.cartons, 'CTN');
  push(parts.sets, 'SET');
  push(parts.pieces, 'PC');
  return list.length ? list.join(' · ') : '0 PC';
}

export function chartPriorityValue(parts: PriorityQty): number {
  if (parts.cartons > 0) return parts.cartons;
  if (parts.sets > 0) return parts.sets;
  return parts.pieces;
}

/** Customer-facing packing contents from stock settings (carton / set / pc). */
export function formatPackagingContent(config?: PackagingLike): string | null {
  const pkg = readPackaging(config);
  if (!pkg || pkg.structureType === 'piece') return null;

  if (pkg.structureType === 'carton_set_piece') {
    const sets = Number(pkg.setsPerCarton || 0);
    const pcsPerSet = Number(pkg.piecesPerSet || 0);
    if (sets <= 0 || pcsPerSet <= 0) return null;
    const total = sets * pcsPerSet;
    return `1 Carton = ${sets} Sets × ${pcsPerSet} PCs (${total} PCs)`;
  }

  const ppc = Number(pkg.piecesPerCarton || 0) || 1;
  if (ppc < 1) return null;
  return `1 Carton = ${ppc} ${ppc === 1 ? 'PC' : 'PCs'}`;
}

/** Compact packing label for product cards, e.g. "12 PC / CTN". */
export function formatPackagingContentCompact(config?: PackagingLike): string | null {
  const pkg = readPackaging(config);
  if (!pkg || pkg.structureType === 'piece') return null;

  if (pkg.structureType === 'carton_set_piece') {
    const sets = Number(pkg.setsPerCarton || 0);
    const pcsPerSet = Number(pkg.piecesPerSet || 0);
    if (sets <= 0 || pcsPerSet <= 0) return null;
    return `${sets} SET × ${pcsPerSet} PC / CTN`;
  }

  const ppc = Number(pkg.piecesPerCarton || 0) || 1;
  if (ppc < 1) return null;
  return `${ppc} PC / CTN`;
}

export function packagingUnitHint(config?: PackagingLike): string | null {
  const pkg = readPackaging(config);
  if (!pkg || pkg.structureType === 'piece') return 'Sold by piece';
  if (pkg.structureType === 'carton_set_piece') {
    const sets = Number(pkg.setsPerCarton || 0);
    const pcsPerSet = Number(pkg.piecesPerSet || 0);
    if (sets <= 0 || pcsPerSet <= 0) return null;
    return `1 Set = ${pcsPerSet} PCs · 1 Carton = ${sets} Sets`;
  }
  const ppc = Number(pkg.piecesPerCarton || 0) || 1;
  if (ppc < 1) return null;
  return `1 Carton = ${ppc} ${ppc === 1 ? 'PC' : 'PCs'}`;
}
